

import React, { useRef, useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Grid, List, ZoomIn, ZoomOut, ArrowLeft, ArrowRight,
  X, Search, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";

const HIDDEN_BOXES_STORAGE_KEY = "viewedImages";
const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Utility functions
const formatToFolderDate = (date) => {
  const yy = date.getFullYear().toString().slice(-2);
  const mm = (date.getMonth() + 1).toString().padStart(2, "0");
  const dd = date.getDate().toString().padStart(2, "0");
  return `${yy}_${mm}_${dd}`;
};

const loadFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(HIDDEN_BOXES_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch (error) {
    console.error("Error loading from localStorage:", error);
    return new Set();
  }
};

const HistoryView = () => {
  // Initialize default date
  const today = new Date();
  const todayFormatted = formatToFolderDate(today);

  // State declarations
  const [currentMonth, setCurrentMonth] = useState(today);
  const [selectedDate, setSelectedDate] = useState(todayFormatted);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredImages, setFilteredImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [hiddenBoxes, setHiddenBoxes] = useState(() => loadFromLocalStorage());
  const [isInitialized, setIsInitialized] = useState(false);

  const calendarRef = useRef(null);

  // API Functions
  const loadAvailableDates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/DefectView/available-dates"
      );
      if (!response.ok) throw new Error('Failed to fetch dates');
      
      const dates = await response.json();
      if (!Array.isArray(dates)) throw new Error('Invalid dates response');

      const sortedDates = [...dates].sort().reverse();
      setAvailableDates(new Set(sortedDates));

      // Select most recent date if not initialized
      if (!isInitialized && sortedDates.length > 0) {
        const mostRecentDate = sortedDates[0];
        setSelectedDate(mostRecentDate);
        await loadImagesForDate(mostRecentDate);
        setIsInitialized(true);
      }
    } catch (error) {
      console.error("Error loading dates:", error);
      setImages([]);
      setFilteredImages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadImagesForDate = async (date) => {
    if (!date) {
      setImages([]);
      setFilteredImages([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/DefectView/images",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date }),
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch images');
      
      const data = await response.json();
      const imageList = Array.isArray(data) ? data : [];
      
      setImages(imageList);
      setFilteredImages(imageList);
    } catch (error) {
      console.error("Error loading images:", error);
      setImages([]);
      setFilteredImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    // Reset initialization state when component mounts
    setIsInitialized(false);
    loadAvailableDates();
  }, []);

  useEffect(() => {
    if (!Array.isArray(images)) {
      setFilteredImages([]);
      return;
    }
    
    const filtered = images.filter(image => 
      image && image.name && image.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredImages(filtered);
  }, [searchTerm, images]);

  // Calendar helpers
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = getDaysInMonth(year, month);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const folderDate = formatToFolderDate(date);
      days.push({
        date: day,
        folderDate,
        hasImages: availableDates.has(folderDate),
        isToday: folderDate === todayFormatted,
      });
    }

    return days;
  };
  const handleOutsideClick = (e) => {
    if (e.target.closest(".max-w-5xl") || e.target.closest(".calendar-sidebar")) {
      return;
    }
    if (selectedImage) {
      setSelectedImage(null);
    }
  };
  // Event handlers remain the same
  const handleImageClick = (image) => {
    if (!image) return;
    
    setHiddenBoxes(prev => {
      const newBoxes = new Set(prev);
      newBoxes.add(image.key);
      try {
        localStorage.setItem(
          HIDDEN_BOXES_STORAGE_KEY, 
          JSON.stringify(Array.from(newBoxes))
        );
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
      return newBoxes;
    });
    
    setSelectedImage(image);
    setZoomLevel(1);
  };

  const handleDateSelect = async (folderDate) => {
    if (!folderDate || !availableDates.has(folderDate)) return;
    
    try {
      setLoading(true);
      setSelectedDate(folderDate);
      
      // Update current month to match selected date
      const [yy, mm, dd] = folderDate.split("_");
      const selectedMonthDate = new Date(2000 + parseInt(yy), parseInt(mm) - 1, parseInt(dd));
      setCurrentMonth(selectedMonthDate);
      
      // Load images for the selected date
      await loadImagesForDate(folderDate);
    } catch (error) {
      console.error("Error selecting date:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBoundingBox = (imageKey, e) => {
    e.stopPropagation();
    setHiddenBoxes(prev => {
      const newBoxes = new Set(prev);
      if (newBoxes.has(imageKey)) {
        newBoxes.delete(imageKey);
      } else {
        newBoxes.add(imageKey);
      }
      try {
        localStorage.setItem(
          HIDDEN_BOXES_STORAGE_KEY, 
          JSON.stringify(Array.from(newBoxes))
        );
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
      return newBoxes;
    });
  };

  const handleZoom = (delta) => {
    setZoomLevel(prev => Math.max(0.1, prev + delta));
  };

  const navigateImage = (direction) => {
    if (!selectedImage || !Array.isArray(filteredImages)) return;
    
    const currentIndex = filteredImages.findIndex(img => img.key === selectedImage.key);
    const newIndex = currentIndex + direction;

    if (newIndex >= 0 && newIndex < filteredImages.length) {
      handleImageClick(filteredImages[newIndex]);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [yy, mm, dd] = dateStr.split("_");
    return `${MONTHS[parseInt(mm) - 1]} ${dd}, 20${yy}`;
  };
  // Calendar Component

  return (
    <div
      className="h-full w-full p-2 sm:p-4 bg-gray-900"
      onClick={handleOutsideClick}
    >
      <div className="max-w-[1400px] mx-auto space-y-4 lg:space-y-0 lg:flex lg:gap-4">
        {/* Main content */}
        <div className="flex-1 order-2 lg:order-1">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base sm:text-xl text-white">
                  {selectedDate ? formatDate(selectedDate) : "Select Date"}
                </CardTitle>
              </div>

              {/* <div className="relative mt-3">
                <Input
                  placeholder="Search images..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                />
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              </div> */}
            </CardHeader>

            <CardContent className="h-[calc(100vh-320px)] lg:h-[calc(100vh-240px)] overflow-y-auto custom-calendar-scrollbar">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4 p-2 sm:p-4">
                  {filteredImages.map((image, index) => (
                    <div
                      key={image.key}
                      className="group relative aspect-square overflow-hidden rounded-lg bg-gray-700 hover:ring-2 hover:ring-blue-500 transition-all"
                    >
                      {index === 0 && (
                        <div className="absolute top-2 right-2 z-20 bg-green-500 text-white px-2 py-1 rounded-md text-xs font-bold">
                          NEW
                        </div>
                      )}
                      <div
                        className="relative w-full h-full cursor-pointer"
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            toggleBoundingBox(image.key, e);
                          } else {
                            handleImageClick(image);
                          }
                        }}
                      >
                        <img
                          src={image.url}
                          alt={image.name}
                          className="object-cover w-full h-full transition-transform group-hover:scale-105"
                        />
                        <div
                          className={`absolute inset-0 border-4 transition-colors duration-200
                          ${!hiddenBoxes.has(image.key) ? "border-green-500" : "border-transparent"}`}
                        />
                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity" />
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-black bg-opacity-75 transform translate-y-full group-hover:translate-y-0 transition-transform">
                          <p className="text-xs sm:text-sm text-white truncate">
                            {image.name}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-300">
                            {new Date(image.lastModified).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedDate && filteredImages.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-400">
                  No images found{searchTerm ? ` matching "${searchTerm}"` : ""}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calendar sidebar */}
        <div className="lg:w-80 order-1 lg:order-2">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() - 1
                      )
                    )
                  }
                  className="text-gray-300 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm sm:text-lg font-medium text-white">
                  {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() + 1
                      )
                    )
                  }
                  className="text-gray-300 hover:text-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-2 sm:p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs sm:text-sm font-medium text-gray-300"
                  >
                    {day.slice(0, 1)}
                  </div>
                ))}
              </div>
       <div className="grid grid-cols-7 gap-1">
  {getCalendarDays().map((day, idx) => (
    <div key={idx} className="aspect-square">
      {day && (
        <button
          onClick={() => day.hasImages && handleDateSelect(day.folderDate)}
          className={`w-full h-full flex items-center justify-center rounded-full text-sm sm:text-base font-medium
            ${day.hasImages ? "hover:bg-blue-500/20 cursor-pointer" : "text-gray-500 cursor-default"}
            ${day.isToday ? "ring-2 ring-green-500 text-green-400" : ""}
            ${day.folderDate === selectedDate ? "bg-blue-500 text-white" : ""}
            ${day.hasImages && !day.isToday && day.folderDate !== selectedDate ? "text-blue-400" : ""}`}
        >
          {day.date}
        </button>
      )}
    </div>
  ))}
</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedImage(null);
            }
          }}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4 sm:p-8">
            {/* Navigation Buttons */}
            <button
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-gray-800 p-1 sm:p-2 rounded-full hover:bg-gray-700 z-50"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(-1);
              }}
              disabled={filteredImages.indexOf(selectedImage) === 0}
            >
              <ArrowLeft className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </button>

            <button
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-gray-800 p-1 sm:p-2 rounded-full hover:bg-gray-700 z-50"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(1);
              }}
              disabled={
                filteredImages.indexOf(selectedImage) ===
                filteredImages.length - 1
              }
            >
              <ArrowRight className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </button>

            {/* Zoom Controls */}
            <div className="absolute top-2 sm:top-4 right-2 sm:right-4 flex gap-1 sm:gap-2 z-50">
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoom(-0.5);
                }}
                className="bg-gray-800 hover:bg-gray-700 h-8 w-8 sm:h-10 sm:w-10"
              >
                <ZoomOut className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoom(0.5);
                }}
                className="bg-gray-800 hover:bg-gray-700 h-8 w-8 sm:h-10 sm:w-10"
              >
                <ZoomIn className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
                className="bg-gray-800 hover:bg-gray-700 h-8 w-8 sm:h-10 sm:w-10"
              >
                <X className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </Button>
            </div>

            {/* Image Container */}
            <figure className="max-w-[95vw] max-h-[80vh] sm:max-w-[90vw] sm:max-h-[90vh] relative">
              {/* Image */}
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="max-w-none transition-transform duration-200"
                style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: "center",
                  cursor: zoomLevel > 1 ? "move" : "default",
                }}
              />

              {/* Caption */}
              <figcaption
                className=" bottom-0 left-1/2 transform -translate-x-1/2 text-white sm:text-lg bg-opacity-60 p-2 rounded bg-slate-900/90 text-center transition-transform duration-200"
                style={{
                  transform: `scale(${Math.min(zoomLevel, 1.5)}) translateY(${zoomLevel < 1 ? selectedImage.size / 2 : zoomLevel > 1.4 ? "80px" : "0"})`,
                  transformOrigin: "center",
                }}
              >
                {"Defect type : " + selectedImage.defectType}
              </figcaption>
            </figure>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryView;
